import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playPing } from "@/lib/sound";
import { ContactUnlock } from "@/components/ContactUnlock";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ShieldAlert, Loader2, MessageSquare, MoreVertical, Ban, Flag, Trash2,
  Search, Plus, Minus, Calendar, HandCoins, CheckCircle2, XCircle, Lock, Check, CheckCheck, Send, Info, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TIME_SLOTS = ["09:00 - 12:00", "12:00 - 15:00", "15:00 - 18:00", "18:00 - 21:00"];
const STEP = 500;
const MAX = 100000;

export default function Messages() {
  const { user, readOnly } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [panel, setPanel] = useState(null); // 'teklif' | null
  const [text, setText] = useState("");
  const [amount, setAmount] = useState(STEP);
  const [custom, setCustom] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [ban, setBan] = useState({ banned: false, remaining_seconds: 0, remaining_violations: 10 });
  const [rulesOpen, setRulesOpen] = useState(false);
  const endRef = useRef(null);
  const lastMsgId = useRef(null);
  const firstLoad = useRef(true);

  const loadConvs = async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      const data = await loadConvs();
      const jobParam = params.get("job");
      const toParam = params.get("to");
      if (jobParam && toParam && user?.id) {
        const pair = [user.id, toParam].sort().join("_");
        const cid = `conv_${jobParam}_${pair}`;
        const existing = data.find((c) => c.conversation_id === cid);
        if (existing) setActive(existing);
        else {
          const job = await api.get(`/jobs/${jobParam}`).then(({ data }) => data).catch(() => null);
          const other = await api.get(`/ustalar/${toParam}`).then(({ data }) => ({ name: data.name })).catch(() => ({ name: "Kullanıcı" }));
          setActive({ conversation_id: cid, other_user_id: toParam, job_id: jobParam, job_title: job?.title, other_name: other.name });
        }
      } else if (toParam && user?.id) {
        const pair = [user.id, toParam].sort().join("_");
        const cid = `conv_direct_${pair}`;
        const existing = data.find((c) => c.conversation_id === cid);
        if (existing) setActive(existing);
        else {
          const other = await api.get(`/ustalar/${toParam}`).then(({ data }) => ({ name: data.name })).catch(() => ({ name: "Kullanıcı" }));
          setActive({ conversation_id: cid, other_user_id: toParam, job_id: "direct", job_title: "Doğrudan Mesaj", other_name: other.name });
        }
      }
    })();
  }, [user]);

  const fetchMessages = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`);
      const last = data[data.length - 1];
      if (last && lastMsgId.current !== last.id) {
        if (!firstLoad.current && last.from_user_id !== user?.id) playPing();
        lastMsgId.current = last.id;
      }
      firstLoad.current = false;
      setMessages((prev) => {
        const same = prev.length === data.length && prev[prev.length - 1]?.id === data[data.length - 1]?.id;
        return same ? prev : data;
      });
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!active?.conversation_id) return;
    firstLoad.current = true;
    lastMsgId.current = null;
    fetchMessages(active.conversation_id);
    const iv = setInterval(() => fetchMessages(active.conversation_id), 2500);
    return () => clearInterval(iv);
  }, [active, fetchMessages]);

  useEffect(() => {
    const iv = setInterval(() => { loadConvs(); }, 5000);
    return () => clearInterval(iv);
  }, []);

  const loadBan = useCallback(async () => {
    try { const { data } = await api.get("/chat/ban-status"); setBan(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadBan(); }, [loadBan]);
  useEffect(() => {
    if (!ban.banned) return;
    const iv = setInterval(() => {
      setBan((b) => {
        const r = Math.max(0, (b.remaining_seconds || 0) - 1);
        return { ...b, remaining_seconds: r, banned: r > 0 };
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [ban.banned]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendAction = async (action_type, extra = {}) => {
    if (!active) return;
    if (readOnly) { toast.error("İzleme (salt-okunur) modunda işlem yapamazsınız"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/messages/action", {
        job_id: active.job_id, to_user_id: active.other_user_id, action_type, ...extra,
      });
      setMessages((m) => [...m, data]);
      setPanel(null); setCustom(false); setAmount(STEP);
      loadConvs();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const sendText = async () => {
    if (!text.trim() || !active) return;
    if (readOnly) { toast.error("İzleme (salt-okunur) modunda işlem yapamazsınız"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/messages", { job_id: active.job_id, to_user_id: active.other_user_id, text });
      setMessages((m) => [...m, data]);
      if (data.masked) {
        if (data.chat_banned_until) {
          toast.error("Kuralları üst üste 10 kez ihlal ettiniz. Mesaj gönderme yetkiniz 1 saat süreyle kısıtlandı.");
          loadBan();
        } else {
          toast.error(`⚠️ Güvenlik kurallarını ihlal ettiniz! Kalan hatalı mesaj hakkınız: ${data.remaining_violations}. Kuralları ihlal etmeye devam ederseniz 1 saat süreyle mesaj gönderemezsiniz.`);
        }
      }
      setText("");
      loadConvs();
    } catch (e) {
      if (e?.response?.status === 429) { loadBan(); toast.error(apiError(e)); }
      else toast.error(apiError(e));
    }
    finally { setSending(false); }
  };

  const sendTeklif = () => {
    if (custom) sendAction("teklif", { is_custom: true });
    else sendAction("teklif", { amount });
  };

  const blockUser = async () => {
    try {
      await api.post("/block", { user_id: active.other_user_id });
      toast.success("Kullanıcı engellendi");
      setActive(null); setMessages([]); loadConvs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteConv = async () => {
    try {
      await api.delete(`/conversations/${active.conversation_id}`);
      toast.success("Sohbet silindi");
      setActive(null); setMessages([]); loadConvs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await api.post("/reports", { target_type: "user", target_id: active.other_user_id, reason: `[Sohbet: ${active.conversation_id}] ${reportReason}` });
      toast.success("Şikayetiniz yöneticilere iletildi");
      setReportOpen(false); setReportReason("");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-extrabold text-slate-900 mb-6">Mesajlarım</h1>
      <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden grid md:grid-cols-[300px_1fr] h-[calc(100vh-11rem)]">
        {/* Conversation list */}
        <div className="border-r border-slate-100 overflow-y-auto p-2 space-y-2">
          {convs.length === 0 ? <p className="p-6 text-sm text-slate-400">Henüz mesaj yok.</p> : convs.map((c) => (
            <button key={c.conversation_id} onClick={() => setActive(c)} data-testid={`conv-${c.conversation_id}`}
              className={`w-full text-left p-3 rounded-2xl border transition-all ${active?.conversation_id === c.conversation_id ? "bg-emerald-50 border-emerald-200 shadow-sm" : "border-slate-100 hover:shadow-md hover:border-slate-200"}`}>
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10"><AvatarImage src={c.other_avatar} /><AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{c.other_name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-900 text-sm truncate">{c.other_name}</span>{c.unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}</div>
                  <div className="text-[11px] text-emerald-600 font-medium truncate">{c.job_title}</div>
                  <div className="text-xs text-slate-400 truncate">{c.last_message}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Chat pane */}
        {active ? (
          <div className="flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
              <button onClick={() => navigate(`/profil/${active.other_user_id}`)} className="flex items-center gap-2 min-w-0 text-left" data-testid="chat-open-profile">
                <Avatar className="h-9 w-9"><AvatarImage src={active.other_avatar} /><AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{active.other_name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate hover:text-emerald-600">{active.other_name}</div>
                  <div className="text-xs text-emerald-600 font-medium truncate">{active.job_title}</div>
                </div>
              </button>
              <div className="flex items-center gap-2">
                <button onClick={() => setRulesOpen(true)} title="Sohbet Kuralları" className="h-9 w-9 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 flex items-center justify-center" data-testid="chat-rules-button"><Info className="h-5 w-5" /></button>
                <div className="hidden sm:block"><ContactUnlock targetUserId={active.other_user_id} jobId={active.job_id} compact /></div>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="chat-menu-button"><MoreVertical className="h-5 w-5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl w-48">
                  <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="chat-report-button"><Flag className="h-4 w-4 mr-2" /> Şikayet Et</DropdownMenuItem>
                  <DropdownMenuItem onClick={blockUser} className="text-amber-600" data-testid="chat-block-button"><Ban className="h-4 w-4 mr-2" /> Kullanıcıyı Engelle</DropdownMenuItem>
                  <DropdownMenuItem onClick={deleteConv} className="text-red-600" data-testid="chat-delete-button"><Trash2 className="h-4 w-4 mr-2" /> Sohbeti Sil</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 rounded-xl p-2.5">
                <ShieldAlert className="h-4 w-4 shrink-0" /> Güvenliğiniz için telefon, adres, sosyal medya ve sayı paylaşımı otomatik gizlenir. Lütfen platform üzerinden iletişim kurun.
              </div>
              {messages.map((m) => {
                const mine = m.from_user_id === user?.id;
                const isAction = m.kind === "action";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${isAction ? (mine ? "bg-emerald-600 text-white" : "bg-white border border-emerald-200 text-slate-800") : (mine ? "brand-gradient text-white" : "bg-slate-100 text-slate-800")} ${isAction ? "font-semibold" : ""}`}>
                      {m.text}
                      {m.original_masked && <span className={`block text-[10px] mt-1 ${mine ? "text-emerald-50" : "text-slate-400"}`}>⚠ İletişim bilgisi gizlendi</span>}
                      {mine && (
                        <span className={`flex items-center justify-end gap-0.5 mt-0.5 ${isAction ? "text-white/70" : "text-emerald-50"}`} data-testid={`msg-tick-${m.read ? "read" : "sent"}`}>
                          {m.read ? <CheckCheck className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {/* Message composer */}
            <div className="border-t border-slate-100 p-3 space-y-2">
              {active.other_deleted ? (
                <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 text-center text-sm text-slate-500 font-medium" data-testid="chat-account-closed">
                  🔒 Bu hesap kapatılmıştır. Yeni mesaj gönderemezsiniz.
                </div>
              ) : ban.banned ? (
                <div className="rounded-xl bg-red-50 border-2 border-red-200 p-4 text-center" data-testid="chat-ban-box">
                  <div className="flex items-center justify-center gap-2 text-red-600 font-bold"><Lock className="h-5 w-5" /> Mesaj gönderme yetkiniz kısıtlandı</div>
                  <p className="text-sm text-red-500 mt-1">Kuralları üst üste 10 kez ihlal ettiğiniz için mesaj gönderme yetkiniz geçici olarak kısıtlanmıştır.</p>
                  <div className="mt-2 text-xs text-red-400 font-semibold">Kalan Süre</div>
                  <div className="text-2xl font-extrabold text-red-600 font-mono" data-testid="chat-ban-countdown">
                    {String(Math.floor(ban.remaining_seconds / 60)).padStart(2, "0")} dk : {String(ban.remaining_seconds % 60).padStart(2, "0")} sn
                  </div>
                </div>
              ) : (
                <>
                  {panel === "teklif" && (
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="teklif-panel">
                      <div className="text-xs font-semibold text-slate-500 mb-2">Teklif tutarı (500 TL katları)</div>
                      {!custom ? (
                        <div className="flex items-center gap-3">
                          <button onClick={() => setAmount((a) => Math.max(STEP, a - STEP))} className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center" data-testid="teklif-minus"><Minus className="h-4 w-4" /></button>
                          <div className="flex-1 text-center text-xl font-extrabold text-emerald-600" data-testid="teklif-amount">{tl(amount)}</div>
                          <button onClick={() => setAmount((a) => Math.min(MAX, a + STEP))} className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center" data-testid="teklif-plus"><Plus className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <div className="text-center text-sm font-bold text-emerald-700 py-2">100.000 TL ve üzeri (Özel Keşif / Proje)</div>
                      )}
                      <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} data-testid="teklif-custom" /> 100.000 TL ve üzeri (Özel Keşif / Proje)
                      </label>
                      <Button onClick={sendTeklif} disabled={sending} className="brand-gradient text-white rounded-xl w-full mt-2 h-10" data-testid="teklif-send">Teklifi Gönder</Button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
                      placeholder="Mesaj yazın (yalnızca harf)..." data-testid="chat-text-input"
                      className="flex-1 h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                    <button onClick={() => setPanel(panel === "teklif" ? null : "teklif")} disabled={sending} data-testid="action-teklif"
                      className={`flex items-center gap-1.5 text-sm font-semibold px-3 h-11 rounded-xl ${panel === "teklif" ? "brand-gradient text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}><HandCoins className="h-4 w-4" /> Teklif Ver</button>
                    <button onClick={sendText} disabled={sending || !text.trim()} data-testid="chat-send-button"
                      className="h-11 w-11 rounded-xl brand-gradient text-white flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="chat-warning-note"><AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" /> ⚠️ Anlamsız mesajlar karşı tarafa iletilmeyecektir.</div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-slate-300">
            <MessageSquare className="h-12 w-12" /><p className="mt-2 text-sm">Bir sohbet seçin</p>
          </div>
        )}
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>Şikayet Et</DialogTitle></DialogHeader>
          <Textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Şikayet sebebinizi yazın..." className="rounded-xl min-h-28" data-testid="report-reason-input" />
          <Button onClick={submitReport} className="brand-gradient text-white rounded-xl" data-testid="report-submit-button">Şikayeti Gönder</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="chat-rules-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-emerald-600" /> Sohbet Kuralları</DialogTitle></DialogHeader>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-2"><span className="text-emerald-600 font-bold">•</span> İletişim bilgisi (telefon, numara vb.) paylaşımı kesinlikle yasaktır.</li>
            <li className="flex gap-2"><span className="text-emerald-600 font-bold">•</span> Sayıların harflerle veya kelimelerle birleşik yazılması sistem tarafından filtrelenir.</li>
            <li className="flex gap-2"><span className="text-emerald-600 font-bold">•</span> Anlamsız harf yığınları ve tek/çift harfli kısa mesajlar gönderilemez.</li>
            <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Kural ihlali içeren mesajları üst üste 10 kez göndermeye çalışan kullanıcıların sohbet erişimi 1 saat süreyle kısıtlanır.</li>
          </ul>
          <Button onClick={() => setRulesOpen(false)} className="brand-gradient text-white rounded-xl mt-2" data-testid="chat-rules-close">Anladım</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
